import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import PwaInstallPrompt from './components/PwaInstallPrompt';
import Layout from './components/Layout/Layout';
const Login = React.lazy(() => import('./pages/Login'));
const RecuperarSenha = React.lazy(() => import('./pages/RecuperarSenha'));
const RedefinirSenha = React.lazy(() => import('./pages/RedefinirSenha'));
const Integracoes = React.lazy(() => import('./pages/Configuracoes/Integracoes'));
const LeadsList = React.lazy(() => import('./pages/Leads/LeadsList'));
const LeadKanban = React.lazy(() => import('./pages/Leads/LeadKanban'));
const LeadDetail = React.lazy(() => import('./pages/Leads/LeadDetail'));
const RegistroVisitas = React.lazy(() => import('./pages/Leads/RegistroVisitas'));
const LeadsReaquecidosList = React.lazy(() => import('./pages/LeadsReaquecidos/LeadsReaquecidosList'));
const EmpreendimentosList = React.lazy(() => import('./pages/Empreendimentos/EmpreendimentosList'));
const EspelhoVendas = React.lazy(() => import('./pages/Empreendimentos/EspelhoVendas'));
const PipelineKanban = React.lazy(() => import('./pages/Pipeline/PipelineKanban'));
const ImovelList = React.lazy(() => import('./pages/Imoveis/ImovelList'));
const ImovelForm = React.lazy(() => import('./pages/Imoveis/ImovelForm'));
const ImovelDetail = React.lazy(() => import('./pages/Imoveis/ImovelDetail'));
const GerarDeUnidade = React.lazy(() => import('./pages/Imoveis/GerarDeUnidade'));
const CorretoresList = React.lazy(() => import('./pages/Vendas/CorretoresList'));
const ParceirosList = React.lazy(() => import('./pages/Vendas/ParceirosList'));
const ParceiroDetail = React.lazy(() => import('./pages/Vendas/ParceiroDetail'));
const CorretoresReaquecidos = React.lazy(() => import('./pages/Vendas/CorretoresReaquecidos'));
const FunilCorretoresKanban = React.lazy(() => import('./pages/Vendas/FunilCorretoresKanban'));
const EventosList = React.lazy(() => import('./pages/Vendas/EventosList'));
const EventoCheckinScanner = React.lazy(() => import('./pages/Vendas/EventoCheckinScanner'));
const EventoPublico = React.lazy(() => import('./pages/Publico/EventoPublico'));
const ConvitePublico = React.lazy(() => import('./pages/Publico/ConvitePublico'));
const AgendamentoPublico = React.lazy(() => import('./pages/Publico/AgendamentoPublico'));
const ImobiliariasList = React.lazy(() => import('./pages/Vendas/ImobiliariasList'));
const ClientesList = React.lazy(() => import('./pages/Vendas/ClientesList'));
const ContratosList = React.lazy(() => import('./pages/Vendas/ContratosList'));
const BoletosList = React.lazy(() => import('./pages/Vendas/BoletosList'));
const PropostasList = React.lazy(() => import('./pages/Propostas/PropostasList'));
const PreReservasList = React.lazy(() => import('./pages/PreReservas/PreReservasList'));
const Blacklist = React.lazy(() => import('./pages/Blacklist/Blacklist'));
const WhatsAppConfig = React.lazy(() => import('./pages/Configuracoes/WhatsApp'));
const AtendimentoPublico = React.lazy(() => import('./pages/Publico/AtendimentoPublico'));
const Elo = React.lazy(() => import('./pages/Vendas/Elo'));
const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const AtendenteIA = React.lazy(() => import('./pages/Configuracoes/AtendenteIA'));
const Site = React.lazy(() => import('./pages/Configuracoes/Site'));
const Usuarios = React.lazy(() => import('./pages/Configuracoes/Usuarios'));
const Roleta = React.lazy(() => import('./pages/Configuracoes/Roleta'));
const FinCobrancas = React.lazy(() => import('./pages/Financeiro/Cobrancas'));
const FinBoletos = React.lazy(() => import('./pages/Financeiro/Boletos'));
const FinReajustes = React.lazy(() => import('./pages/Financeiro/Reajustes'));
const FinContasAPagar = React.lazy(() => import('./pages/Financeiro/ContasAPagar'));
const FinDocumentos = React.lazy(() => import('./pages/Financeiro/Documentos'));
const FinNotasFiscais = React.lazy(() => import('./pages/Financeiro/NotasFiscais'));
const FinFaturamento = React.lazy(() => import('./pages/Financeiro/Faturamento'));
const FinFluxoCaixa = React.lazy(() => import('./pages/Financeiro/FluxoCaixa'));
const FinMovimentos = React.lazy(() => import('./pages/Financeiro/Movimentos'));
const FinContas = React.lazy(() => import('./pages/Financeiro/Contas'));
const FinPainel = React.lazy(() => import('./pages/Financeiro/Painel'));
const FinRecorrencias = React.lazy(() => import('./pages/Financeiro/Recorrencias'));
const FinConciliacao = React.lazy(() => import('./pages/Financeiro/Conciliacao'));
const FinAuditoria = React.lazy(() => import('./pages/Financeiro/Auditoria'));
const SiteEditor = React.lazy(() => import('./pages/Configuracoes/SiteEditor'));

function getUser() {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
}

function PrivateRoute({ children }) {
  return localStorage.getItem('token') ? children : <Navigate to="/login" />;
}

// todos entram pelo dashboard; para o corretor interno o servidor devolve só os dados dele
function getDefaultPath(user) {
  return user ? '/dashboard' : '/login';
}

function GerenteRoute({ children }) {
  const user = getUser();
  if (!user) return <Navigate to="/login" />;
  return ['admin', 'gerente'].includes(user.perfil) ? children : <Navigate to="/leads" replace />;
}

// Financeiro: perfis admin, gerente e financeiro; quem tem módulos restritos precisa do módulo 'financeiro'
function FinanceiroRoute({ children }) {
  const user = getUser();
  if (!user) return <Navigate to="/login" />;
  if (!['admin', 'gerente', 'financeiro'].includes(user.perfil)) return <Navigate to={getDefaultPath(user)} replace />;
  return <ModuleRoute modulo="financeiro">{children}</ModuleRoute>;
}

function ModuleRoute({ modulo, children }) {
  const user = getUser();
  if (!user) return <Navigate to="/login" />;
  if (user.perfil === 'admin') return children;
  const mods = user.modulos_permitidos;
  if (!mods) return children;
  if (mods.includes(modulo) || mods.some(m => m.startsWith(modulo + '.'))) return children;
  return <Navigate to={getDefaultPath(user)} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <PwaInstallPrompt />
      <React.Suspense fallback={<div className="p-8 text-center text-gray-400">Carregando…</div>}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/recuperar-senha" element={<RecuperarSenha />} />
        <Route path="/redefinir-senha" element={<RedefinirSenha />} />
        <Route path="/publico/eventos/:id" element={<EventoPublico />} />
        <Route path="/publico/convite/:token" element={<ConvitePublico />} />
        <Route path="/publico/atendimento" element={<AtendimentoPublico />} />
        <Route path="/publico/agendamento/:token" element={<AgendamentoPublico />} />
        <Route path="/vendas/eventos/checkin" element={<EventoCheckinScanner />} />
        <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route index element={<Navigate to={getDefaultPath(getUser())} replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          {/* Menu CRM */}
          <Route path="leads" element={<ModuleRoute modulo="leads"><LeadsList /></ModuleRoute>} />
          <Route path="leads/kanban" element={<ModuleRoute modulo="leads"><LeadKanban /></ModuleRoute>} />
          <Route path="leads/visitas" element={<ModuleRoute modulo="leads"><RegistroVisitas /></ModuleRoute>} />
          <Route path="leads/:id" element={<ModuleRoute modulo="leads"><LeadDetail /></ModuleRoute>} />
          <Route path="leads-reaquecidos" element={<ModuleRoute modulo="leads"><LeadsReaquecidosList /></ModuleRoute>} />
          <Route path="blacklist" element={<ModuleRoute modulo="envios_massa"><Blacklist /></ModuleRoute>} />
          <Route path="empreendimentos" element={<ModuleRoute modulo="empreendimentos"><EmpreendimentosList /></ModuleRoute>} />
          <Route path="empreendimentos/:id/espelho" element={<ModuleRoute modulo="empreendimentos"><EspelhoVendas /></ModuleRoute>} />
          <Route path="imoveis" element={<ModuleRoute modulo="imoveis"><ImovelList /></ModuleRoute>} />
          <Route path="imoveis/novo" element={<ModuleRoute modulo="imoveis"><ImovelForm /></ModuleRoute>} />
          <Route path="imoveis/gerar" element={<ModuleRoute modulo="imoveis"><GerarDeUnidade /></ModuleRoute>} />
          <Route path="imoveis/:id" element={<ModuleRoute modulo="imoveis"><ImovelDetail /></ModuleRoute>} />
          <Route path="imoveis/:id/editar" element={<ModuleRoute modulo="imoveis"><ImovelForm /></ModuleRoute>} />
          <Route path="pipeline" element={<ModuleRoute modulo="pipeline"><PipelineKanban /></ModuleRoute>} />
          {/* Configurações (qualquer usuário logado) */}
          <Route path="configuracoes/whatsapp" element={<WhatsAppConfig />} />
          <Route path="configuracoes/integracoes" element={<GerenteRoute><Integracoes /></GerenteRoute>} />
          <Route path="configuracoes/roleta" element={<GerenteRoute><ModuleRoute modulo="roleta"><Roleta /></ModuleRoute></GerenteRoute>} />
          <Route path="configuracoes/usuarios" element={<GerenteRoute><Usuarios /></GerenteRoute>} />
          <Route path="configuracoes/atendente-ia" element={<GerenteRoute><AtendenteIA /></GerenteRoute>} />
          <Route path="configuracoes/site" element={<GerenteRoute><Site /></GerenteRoute>} />
          <Route path="configuracoes/site/:site/:arquivo" element={<GerenteRoute><React.Suspense fallback={<div className="p-6 text-gray-500">Carregando editor…</div>}><SiteEditor /></React.Suspense></GerenteRoute>} />
          {/* Menu Parcerias */}
          <Route path="vendas/corretores" element={<ModuleRoute modulo="propostas"><CorretoresList /></ModuleRoute>} />
          <Route path="vendas/parceiros" element={<ModuleRoute modulo="propostas"><ParceirosList /></ModuleRoute>} />
          <Route path="vendas/parceiros/:corretorId" element={<ModuleRoute modulo="propostas"><ParceiroDetail /></ModuleRoute>} />
          <Route path="vendas/funil-corretores" element={<ModuleRoute modulo="propostas"><FunilCorretoresKanban /></ModuleRoute>} />
          <Route path="vendas/elo" element={<ModuleRoute modulo="propostas"><Elo /></ModuleRoute>} />
          <Route path="vendas/eventos" element={<ModuleRoute modulo="propostas"><EventosList /></ModuleRoute>} />
          <Route path="vendas/corretores-reaquecidos" element={<ModuleRoute modulo="propostas"><CorretoresReaquecidos /></ModuleRoute>} />
          <Route path="vendas/imobiliarias" element={<ModuleRoute modulo="propostas"><ImobiliariasList /></ModuleRoute>} />
          <Route path="vendas/clientes" element={<ModuleRoute modulo="propostas"><ClientesList /></ModuleRoute>} />
          <Route path="vendas/pre-reservas" element={<ModuleRoute modulo="propostas"><PreReservasList /></ModuleRoute>} />
          <Route path="vendas/propostas" element={<ModuleRoute modulo="propostas"><PropostasList /></ModuleRoute>} />
          <Route path="vendas/contratos" element={<ModuleRoute modulo="propostas"><ContratosList /></ModuleRoute>} />
          <Route path="vendas/boletos" element={<ModuleRoute modulo="propostas"><BoletosList /></ModuleRoute>} />
          <Route path="financeiro" element={<Navigate to="/financeiro/cobrancas" replace />} />
          <Route path="financeiro/cobrancas" element={<FinanceiroRoute><FinCobrancas /></FinanceiroRoute>} />
          <Route path="financeiro/boletos" element={<FinanceiroRoute><FinBoletos /></FinanceiroRoute>} />
          <Route path="financeiro/reajustes" element={<FinanceiroRoute><FinReajustes /></FinanceiroRoute>} />
          <Route path="financeiro/contas-a-pagar" element={<FinanceiroRoute><FinContasAPagar /></FinanceiroRoute>} />
          <Route path="financeiro/documentos" element={<FinanceiroRoute><FinDocumentos /></FinanceiroRoute>} />
          <Route path="financeiro/nfe" element={<FinanceiroRoute><FinNotasFiscais /></FinanceiroRoute>} />
          <Route path="financeiro/faturamento" element={<FinanceiroRoute><FinFaturamento /></FinanceiroRoute>} />
          <Route path="financeiro/fluxo-de-caixa" element={<FinanceiroRoute><FinFluxoCaixa /></FinanceiroRoute>} />
          <Route path="financeiro/movimentos" element={<FinanceiroRoute><FinMovimentos /></FinanceiroRoute>} />
          <Route path="financeiro/painel" element={<FinanceiroRoute><FinPainel /></FinanceiroRoute>} />
          <Route path="financeiro/recorrencias" element={<FinanceiroRoute><FinRecorrencias /></FinanceiroRoute>} />
          <Route path="financeiro/conciliacao" element={<FinanceiroRoute><FinConciliacao /></FinanceiroRoute>} />
          <Route path="financeiro/auditoria" element={<FinanceiroRoute><FinAuditoria /></FinanceiroRoute>} />
          <Route path="financeiro/contas" element={<FinanceiroRoute><FinContas /></FinanceiroRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </React.Suspense>
    </BrowserRouter>
  );
}
